// src/components/Settings/Dialogs/UploadDocumentDialog.tsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  LinearProgress,
  Theme,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

interface UploadDocumentDialogProps {
  uploadOpen: boolean;
  setUploadOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  uploadProgress: number;
  handleFileUpload: () => void;
  theme: Theme;
}

const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({
  uploadOpen,
  setUploadOpen,
  selectedFile,
  setSelectedFile,
  uploadProgress,
  handleFileUpload,
  theme,
}) => {
  return (
    <Dialog
      open={uploadOpen}
      onClose={() => {
        setUploadOpen(false);
        setSelectedFile(null);
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          background: theme.palette.background.paper,
          padding: "16px",
        },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <CloudUploadOutlinedIcon
            style={{ marginBottom: 16, color: theme.palette.primary.main }}
          />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            อัพโหลดเอกสารใหม่
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box width="100%" sx={{ mt: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            sx={{ gap: 2, mb: 2 }}
          >
            <Button variant="contained" component="label" fullWidth>
              เลือกไฟล์
              <input
                hidden
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setSelectedFile(file);
                }}
              />
            </Button>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="row"
              sx={{
                gap: 1,
                p: 2,
                borderRadius: 1,
                backgroundColor: selectedFile?.name
                  ? `${theme.palette.success.main}22`
                  : `${theme.palette.text.secondary}22`,
                width: "100%",
              }}
            >
              {selectedFile?.name ? (
                <CheckOutlinedIcon color="success" sx={{ fontSize: 16 }} />
              ) : (
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
              )}

              <Typography
                variant="body2"
                color={selectedFile?.name ? "textPrimary" : "textSecondary"}
              >
                {selectedFile?.name
                  ? selectedFile?.name
                  : "กรุณาเลือกไฟล์ที่ต้องการอัพโหลด"}
              </Typography>
            </Box>
          </Box>

          {uploadProgress > 0 && (
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {Math.round(uploadProgress)}% Uploaded
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="center" width="100%" gap={2}>
          <Button
            onClick={() => {
              setUploadOpen(false);
              setSelectedFile(null);
            }}
            color="primary"
            variant="outlined"
            fullWidth
          >
            ยกเลิก
          </Button>

          <Button
            onClick={handleFileUpload}
            color="primary"
            variant="contained"
            fullWidth
            disabled={!selectedFile}
          >
            อัพโหลด
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentDialog;
